import { useState, useEffect } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../../../firebase';

interface Certificate {
  id: number;
  name: string;
  image: string;
}

export default function StorySection() {
  const [aboutData, setAboutData] = useState({
    title: 'Temellerinde Güven,\nGeleceğinde Vizyon Var.',
    storyPart1: 'KARE YAPI İNŞAAT, 15 yılı aşkın süredir Türkiye\'nin inşaat sektöründe kalite, güven ve mimari estetiği yeniden tanımlayan köklü bir A Sınıfı Müteahhitlik markasıdır. Kurulduğumuz ilk günden bu yana, sadece binalar değil; nesilden nesile güvenle aktarılacak devasa yaşam merkezleri, prestijli ticari alanlar ve mutlu aile yuvaları inşa ediyoruz.',
    storyPart2: 'Her projemizde "İnsan Odaklı Gelişim" felsefesini kalbimize koyuyor, İstanbul\'un en değerli lokasyonlarında arazinin ruhuna uygun, doğayla barışık ve en ileri mühendislik teknolojilerini kullanan anıtsal yapılar yükseltiyoruz. Bizim için bir inşaat; beton ve demirden ibaret değil, uzun ömürlü bir sanat eseridir.',
    companyImage: '',
    certificates: [
      { id: 1, name: 'ISO 9001:2015 Kalite Yönetimi', image: '' },
      { id: 2, name: 'ISO 14001:2015 Çevre Yönetimi', image: '' },
      { id: 3, name: 'OHSAS 18001 İş Sağlığı ve Güvenliği', image: '' }
    ] as Certificate[]
  });

  useEffect(() => {
    const unsub = onSnapshot(doc(db, 'pages', 'about'), (doc) => {
      if (doc.exists()) {
        const data = doc.data();
        
        const fallbackP1 = 'KARE YAPI İNŞAAT, 15 yılı aşkın süredir Türkiye\'nin inşaat sektöründe kalite, güven ve mimari estetiği yeniden tanımlayan köklü bir A Sınıfı Müteahhitlik markasıdır. Kurulduğumuz ilk günden bu yana, sadece binalar değil; nesilden nesile güvenle aktarılacak devasa yaşam merkezleri, prestijli ticari alanlar ve mutlu aile yuvaları inşa ediyoruz.';
        const fallbackP2 = 'Her projemizde "İnsan Odaklı Gelişim" felsefesini kalbimize koyuyor, İstanbul\'un en değerli lokasyonlarında arazinin ruhuna uygun, doğayla barışık ve en ileri mühendislik teknolojilerini kullanan anıtsal yapılar yükseltiyoruz. Bizim için bir inşaat; beton ve demirden ibaret değil, uzun ömürlü bir sanat eseridir.';

        const isCorruptP1 = data.storyPart1 && data.storyPart1.includes('KARE YAPI İNŞAAT, sektördeki köklü geçmişi');
        const isCorruptP2 = data.storyPart2 && data.storyPart2.includes('Her projemizde "önce insan"');

        setAboutData({
          title: data.title || 'Temellerinde Güven,\nGeleceğinde Vizyon Var.',
          storyPart1: isCorruptP1 ? fallbackP1 : (data.storyPart1 || fallbackP1),
          storyPart2: isCorruptP2 ? fallbackP2 : (data.storyPart2 || fallbackP2),
          companyImage: data.companyImage || '',
          certificates: data.certificates || []
        });
      }
    });
    return () => unsub();
  }, []);

  const titleLines = aboutData.title.split('\n');

  return (
    <section className="py-20 px-4 bg-white">
      <div className="max-w-7xl mx-auto">
        
        {/* Kurumsal Hikaye Kısmı */}
        <div className="grid md:grid-cols-2 gap-16 items-start mb-24">
          <div>
            <div className="inline-block px-4 py-1.5 bg-red-50 border border-red-100 text-red-600 font-semibold rounded-full mb-6 text-sm">
              Kurumsal Profil
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-8 tracking-tight leading-tight whitespace-pre-line" style={{ fontFamily: 'Raleway, sans-serif' }}>
              {aboutData.title}
            </h2>
            <div className="space-y-5 text-slate-600 text-lg leading-relaxed">
              <p>{aboutData.storyPart1}</p>
              <p>{aboutData.storyPart2}</p>
            </div>
          </div>
          
          <div className="relative mt-8 md:mt-0">
            <div className="relative h-[650px] rounded-2xl overflow-hidden shadow-2xl group">
              <img
                src={aboutData.companyImage || '/premium-project-block.png'}
                alt="KARE YAPI İNŞAAT Tamamlanmış Proje"
                className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-transparent to-transparent pointer-events-none"></div>

              {/* Orijinal Şeffaf Logo Watermark */}
              <div className="absolute top-6 right-6 z-10 opacity-90 pointer-events-none drop-shadow-md">
                <img 
                  src="https://static.readdy.ai/image/462a574349d19c517babfc55bf63e079/c8ac21bc970c8b7e4f4c5b80839b9d5d.png" 
                  alt="KARE YAPI" 
                  className="h-8 md:h-12 w-auto object-contain"
                />
              </div>
            </div>
            
            {/* Tecrübe Rozeti */}
            <div className="absolute -bottom-8 -left-8 bg-gradient-to-br from-red-600 to-red-700 text-white p-10 rounded-2xl shadow-2xl max-w-[280px]">
              <div className="text-6xl font-black mb-2 tracking-tighter" style={{ fontFamily: 'Raleway, sans-serif' }}>15+</div>
              <div className="text-xl font-medium leading-snug">Yıllık Zirve <br/> Deneyimi</div>
              <div className="w-12 h-1 bg-white/30 rounded-full mt-4"></div>
            </div>
          </div>
        </div>

        {/* Sertifikalar ve ISO Belgeleri Kısmı */}
        <div className="mt-20 pt-16 border-t border-slate-100">
          <div className="text-center mb-16">
            <h3 className="text-3xl font-bold text-slate-900 mb-4 tracking-tight" style={{ fontFamily: 'Raleway, sans-serif' }}>Tescilli Kalitemiz & Sertifikalarımız</h3>
            <p className="text-slate-500 max-w-2xl mx-auto text-lg">
              KARE YAPI olarak üretim bandından anahtar teslimine kadar geçen tüm süreçlerimiz, uluslararası bağımsız denetim kuruluşları tarafından onaylanmış belgelere sahiptir.
            </p>
            <div className="w-16 h-1 bg-red-600 mx-auto mt-6 rounded-full"></div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {aboutData.certificates.map((cert) => (
              <div key={cert.id} className="bg-slate-50 hover:bg-white border border-slate-100 hover:border-red-100 p-8 rounded-2xl transition-all duration-300 hover:shadow-xl hover:-translate-y-1 group">
                <div className="w-16 h-16 bg-white rounded-2xl border border-slate-200 shadow-sm flex items-center justify-center mb-6 group-hover:scale-110 transition-transform overflow-hidden">
                  {cert.image ? (
                     <img src={cert.image} alt={cert.name} className="w-full h-full object-contain" />
                  ) : (
                     <i className="ri-award-fill text-3xl text-red-600"></i>
                  )}
                </div>
                <h4 className="font-bold text-slate-900 text-lg mb-2">{cert.name}</h4>
              </div>
            ))}
          </div>
          
          <div className="mt-12 bg-red-50 border border-red-100 rounded-2xl p-6 flex items-center gap-6 justify-center text-center md:text-left flex-col md:flex-row">
            <div className="w-16 h-16 bg-red-600 text-white rounded-full flex items-center justify-center flex-shrink-0">
              <i className="ri-checkbox-circle-fill text-3xl"></i>
            </div>
            <div>
               <h5 className="text-lg font-bold text-slate-900">Yapı Denetim ve Zemin Güvenliği %100 Standardı</h5>
               <p className="text-slate-600 text-sm mt-1 max-w-2xl">Bütün projelerimiz bağımsız yapı denetim kuruluşlarınca 7/24 denetlenmekte olup, en ağır zemin etüt (geoteknik) raporlarından ve sismik mukavemet testlerinden kusursuz başarıyla geçmiştir.</p>
            </div>
          </div>

        </div>

      </div>
    </section>
  );
}